import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import AdminDashboard from "../../Admin/Dashboard/Dashboard";
import { Container } from "@mui/material";
import './NewLaunchAd.css';

const NewLaunchAd = () => {
    const { id } = useParams();
    const [propertyData, setPropertyData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(`https://landnest.net:81/ad-property/${id}/`);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                setPropertyData(data);
            } catch (err) {
                setError(err.message || 'Failed to fetch property data');
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchData();
        } else {
            setError('No property ID provided');
            setLoading(false);
        }
    }, [id]);

    if (loading) {
        return (
            <Container maxWidth={false} sx={{ mt: 4 }}>
                <AdminDashboard />
                <div className="web-container">
                    <div className="loading">Loading property data...</div>
                </div>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth={false} sx={{ mt: 4 }}>
                <AdminDashboard />
                <div className="web-container">
                    <div className="error">Error: {error}</div>
                </div>
            </Container>
        );
    }

    if (!propertyData) {
        return (
            <Container maxWidth={false} sx={{ mt: 4 }}>
                <AdminDashboard />
                <div className="web-container">
                    <div className="no-data">No property data available</div>
                </div>
            </Container>
        );
    }

    const formatPrice = (price) => {
        if (!price) return 'N/A';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0,
        }).format(price);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Not Set';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'Not Set';
        const date = new Date(dateString);
        return date.toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Get property name or generate from category
    const getPropertyName = () => {
        if (propertyData.property_name) {
            return propertyData.property_name;
        }
        return `${propertyData.category_name || 'Property'} ${propertyData.property_id || ''}`;
    };

    // Get BHK info
    const getBHKInfo = () => {
        if (propertyData.bedrooms_count) {
            return `${propertyData.bedrooms_count} BHK`;
        }
        if (propertyData._1bhk_count) return `${propertyData._1bhk_count} BHK`;
        if (propertyData._2bhk_count) return `${propertyData._2bhk_count} BHK`;
        if (propertyData._3bhk_count) return `${propertyData._3bhk_count} BHK`;
        if (propertyData._4bhk_count) return `${propertyData._4bhk_count} BHK`;
        return 'N/A';
    };

    // Get area range
    const getAreaRange = () => {
        if (propertyData.min_acres && propertyData.max_acres) {
            return `${propertyData.min_acres} - ${propertyData.max_acres} acres`;
        }
        if (propertyData.site_area) {
            return `${propertyData.site_area} ${propertyData.units || 'sq ft'}`;
        }
        return 'N/A';
    };

    // Get all fields with values
    const getFieldsWithValues = () => {
        const fields = {
            'Property ID': propertyData.property_id,
            'Property Name': getPropertyName(),
            'Category': propertyData.category_name,
            'Type': propertyData.type ? propertyData.type.replace('-', ' ').toUpperCase() : 'N/A',
            'Price': formatPrice(propertyData.price),
            'Location': propertyData.location,
            'Latitude': propertyData.lat,
            'Longitude': propertyData.long,
            'Facing': propertyData.facing,
            'Road Width': propertyData.roadwidth ? `${propertyData.roadwidth} ${propertyData.units || ''}` : 'N/A',
            'Site Area': propertyData.site_area ? `${propertyData.site_area} ${propertyData.units || ''}²` : 'N/A',
            'Length': propertyData.length,
            'Width': propertyData.width,
            'Units': propertyData.units,
            'BVE': getBHKInfo(),
            'Bathrooms': propertyData.bathrooms_count,
            'Posted By': propertyData.posted_by,
            'User First Name': propertyData.user_first_name,
            'User Last Name': propertyData.user_last_name,
            'User Mobile': propertyData.user_mobile_no,
            'User Email': propertyData.user_email,
            'Mobile No': propertyData.mobile_no,
            'Power Backup': propertyData.power_backup,
            'Gated Security': propertyData.gated_security,
            'Parking': propertyData.parking || 'N/A',
            'Lift': propertyData.lift || 'N/A',
            'Description': propertyData.description,
            'Admin Status': propertyData.Admin_status,
            'Status': propertyData.status ? 'Active' : 'Inactive',
            'Created At': formatDateTime(propertyData.created_at),
            'Updated At': formatDateTime(propertyData.updated_at),
            'Ad Start Date': propertyData.adstart_date ? formatDate(propertyData.adstart_date) : 'Not Set',
            'Ad End Date': propertyData.adend_date ? formatDate(propertyData.adend_date) : 'Not Set',
            'User ID': propertyData.user_id,
            'Category ID': propertyData.category_id,
        };

        // Filter out null, undefined, empty string values
        const filteredFields = {};
        Object.keys(fields).forEach(key => {
            const value = fields[key];
            if (value !== null && value !== undefined && value !== '' && value !== 'N/A') {
                filteredFields[key] = value;
            }
        });
        return filteredFields;
    };

    const fieldsWithValues = getFieldsWithValues();

    return (
        <Container maxWidth={false} sx={{ mt: 4 }}>
            <AdminDashboard />
            <div className="web-container">
                {/* Main Content - Split Layout */}
                <div className="split-layout">
                    {/* Left Side - Single Card with All Fields */}
                    <div className="property-info-section">
                        <div className="single-card">
                            <div className="card-header">
                                <h2>{getPropertyName()}</h2>
                                <div className="header-tags">
                                    {propertyData.type && (
                                        <span className="tag new-launch">
                                            {propertyData.type.replace('-', ' ').toUpperCase()}
                                        </span>
                                    )}
                                    <span className="tag featured">Featured</span>
                                    <span className="tag recommended">Recommended</span>
                                    <span className={`tag status ${propertyData.Admin_status?.toLowerCase() || 'pending'}`}>
                                        {propertyData.Admin_status || 'Pending'}
                                    </span>
                                </div>
                            </div>

                            <div className="card-body">
                                <div className="fields-grid">
                                    {Object.entries(fieldsWithValues).map(([key, value]) => (
                                        <div key={key} className="field-item">
                                            <label>{key}</label>
                                            <p className={key === 'Price' ? 'price-value' : ''}>
                                                {value}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Side - Mobile Emulator */}
                    <div className="emulator-section">
                        <div className="emulator-header">
                            <h3>User Panel Preview</h3>
                        </div>

                        <div className="device-frame">
                            {/* Device Notch */}
                            <div className="device-notch">
                                <div className="notch-content">
                                    <span className="time">9:41</span>
                                    <div className="notch-icons">
                                        <span className="signal">📶</span>
                                        <span className="wifi">📶</span>
                                        <span className="battery">🔋</span>
                                    </div>
                                </div>
                            </div>

                            {/* Mobile Content */}
                            <div className="mobile-content">
                                {/* Header */}
                                <div className="mobile-header">
                                    <div className="header-top">
                                        <button className="back-btn">←</button>
                                        <h2>{getPropertyName().substring(0, 20)}</h2>
                                        <button className="menu-btn">⋮</button>
                                    </div>
                                    
                                    <div className="property-badges">
                                        {propertyData.type && (
                                            <span className="badge new-launch">
                                                {propertyData.type.replace('-', ' ').toUpperCase()}
                                            </span>
                                        )}
                                        <span className="badge featured">Featured</span>
                                        <span className="badge recommended">Recommended</span>
                                    </div>
                                </div>

                                {/* Property Image */}
                                <div className="property-image-wrapper">
                                    {propertyData.AdProperty_images && propertyData.AdProperty_images.length > 0 ? (
                                        <img
                                            src={`https://landnest.net:81${propertyData.AdProperty_images[0].image}`}
                                            alt="Property"
                                            className="property-image"
                                            onError={(e) => {
                                                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZTBlMGUwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxOHB4IiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+Tm8gSW1hZ2U8L3RleHQ+PC9zdmc+';
                                            }}
                                        />
                                    ) : (
                                        <div className="no-image">No Image Available</div>
                                    )}
                                </div>

                                {/* Details Card */}
                                <div className="details-card">
                                    <div className="property-title-section">
                                        <h3>{getPropertyName()}</h3>
                                        <span className="property-type-badge">{propertyData.category_name || 'Category'}</span>
                                    </div>

                                    <div className="price-section">
                                        <span className="price-label">Price</span>
                                        <span className="price-value">{formatPrice(propertyData.price)}</span>
                                    </div>

                                    <div className="details-grid">
                                        <div className="detail-item">
                                            <span className="detail-icon">📍</span>
                                            <div className="detail-content">
                                                <span className="detail-label">Location</span>
                                                <span className="detail-value">{propertyData.location || 'N/A'}</span>
                                            </div>
                                        </div>

                                        <div className="detail-item">
                                            <span className="detail-icon">👤</span>
                                            <div className="detail-content">
                                                <span className="detail-label">Posted by</span>
                                                <span className="detail-value">{propertyData.posted_by || 'N/A'}</span>
                                            </div>
                                        </div>

                                        <div className="detail-item">
                                            <span className="detail-icon">📐</span>
                                            <div className="detail-content">
                                                <span className="detail-label">Site Area</span>
                                                <span className="detail-value">{propertyData.site_area || 'N/A'} {propertyData.units || ''}²</span>
                                            </div>
                                        </div>

                                        <div className="detail-item">
                                            <span className="detail-icon">🧭</span>
                                            <div className="detail-content">
                                                <span className="detail-label">Facing</span>
                                                <span className="detail-value">{propertyData.facing || 'N/A'}</span>
                                            </div>
                                        </div>

                                        <div className="detail-item">
                                            <span className="detail-icon">🛣️</span>
                                            <div className="detail-content">
                                                <span className="detail-label">Road Width</span>
                                                <span className="detail-value">{propertyData.roadwidth || 'N/A'} {propertyData.units || ''}</span>
                                            </div>
                                        </div>

                                        <div className="detail-item">
                                            <span className="detail-icon">📏</span>
                                            <div className="detail-content">
                                                <span className="detail-label">Dimensions</span>
                                                <span className="detail-value">{propertyData.length || 'N/A'} × {propertyData.width || 'N/A'} {propertyData.units || ''}</span>
                                            </div>
                                        </div>

                                        <div className="detail-item">
                                            <span className="detail-icon">🚿</span>
                                            <div className="detail-content">
                                                <span className="detail-label">Bathrooms</span>
                                                <span className="detail-value">{propertyData.bathrooms_count || 'N/A'}</span>
                                            </div>
                                        </div>

                                        <div className="detail-item">
                                            <span className="detail-icon">⚡</span>
                                            <div className="detail-content">
                                                <span className="detail-label">Power Backup</span>
                                                <span className="detail-value">{propertyData.power_backup || 'N/A'}</span>
                                            </div>
                                        </div>

                                        <div className="detail-item">
                                            <span className="detail-icon">🔒</span>
                                            <div className="detail-content">
                                                <span className="detail-label">Gated Security</span>
                                                <span className="detail-value">{propertyData.gated_security || 'N/A'}</span>
                                            </div>
                                        </div>

                                        <div className="detail-item">
                                            <span className="detail-icon">🅿️</span>
                                            <div className="detail-content">
                                                <span className="detail-label">Parking</span>
                                                <span className="detail-value">{propertyData.parking || 'N/A'}</span>
                                            </div>
                                        </div>

                                        <div className="detail-item">
                                            <span className="detail-icon">⬆️</span>
                                            <div className="detail-content">
                                                <span className="detail-label">Lift</span>
                                                <span className="detail-value">{propertyData.lift || 'N/A'}</span>
                                            </div>
                                        </div>

                                        <div className="detail-item">
                                            <span className="detail-icon">📅</span>
                                            <div className="detail-content">
                                                <span className="detail-label">Ad Start</span>
                                                <span className="detail-value">{propertyData.adstart_date ? formatDate(propertyData.adstart_date) : 'Not Set'}</span>
                                            </div>
                                        </div>

                                        <div className="detail-item">
                                            <span className="detail-icon">📅</span>
                                            <div className="detail-content">
                                                <span className="detail-label">Ad End</span>
                                                <span className="detail-value">{propertyData.adend_date ? formatDate(propertyData.adend_date) : 'Not Set'}</span>
                                            </div>
                                        </div>

                                        <div className="detail-item">
                                            <span className="detail-icon">📋</span>
                                            <div className="detail-content">
                                                <span className="detail-label">Status</span>
                                                <span className="detail-value">{propertyData.Admin_status || 'Pending'}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {propertyData.description && (
                                        <div className="description-section">
                                            <span className="description-label">Description</span>
                                            <p className="description-text">{propertyData.description}</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Bottom Navigation */}
                            <div className="mobile-footer">
                                <div className="footer-menu">
                                    <div className="menu-item active">
                                        <span className="menu-icon">🏠</span>
                                        <span className="menu-label">Home</span>
                                    </div>
                                    <div className="menu-item">
                                        <span className="menu-icon">📋</span>
                                        <span className="menu-label">Listing</span>
                                    </div>
                                    <div className="menu-item">
                                        <span className="menu-icon">📊</span>
                                        <span className="menu-label">Procurement</span>
                                    </div>
                                    <div className="menu-item">
                                        <span className="menu-icon">👤</span>
                                        <span className="menu-label">Profile</span>
                                    </div>
                                </div>
                            </div>

                            {/* Home Indicator */}
                            <div className="home-indicator"></div>
                        </div>
                    </div>
                </div>
            </div>
        </Container>
    );
};

export default NewLaunchAd;